import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faTruck, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config/api.config';
import BackButton from './BackButton';
import LoginPrompt from './LoginPrompt';
import './MyOrders.css';

const STATUS_STEPS = ['placed', 'shipped', 'delivered'];

const payLabel = (method) => {
  if (method === 'online' || method === 'easypaisa' || method === 'jazzcash') {
    return 'Online Payment';
  }
  return 'Cash on Delivery';
};

const paymentStatusLabel = (order) => {
  const method = order.payment_method || 'cod';
  const status = (order.payment_status || '').toLowerCase();
  if (status === 'paid') return 'Paid';
  if (status === 'pending_verification') return 'Pending verification';
  if (method === 'cod') return 'Pay on delivery';
  return 'Unpaid';
};

const statusIcon = (status) => {
  if (status === 'delivered') return faCheckCircle;
  if (status === 'shipped') return faTruck;
  return faBoxOpen;
};

const MyOrders = ({ onBackClick, onLoginClick }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    setUser(storedUser);
    if (storedUser) {
      fetchOrders(storedUser.id);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchOrders = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}?action=orders&userId=${userId}`);
      const data = await response.json();
      if (data.status === 'ok') {
        setOrders(data.orders || []);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <LoginPrompt
        icon="📦"
        title="My Orders"
        message="Please login to view your orders"
        onLoginClick={onLoginClick}
        onBackClick={onBackClick}
      />
    );
  }

  if (loading) {
    return (
      <div className="my-orders-page">
        <div className="loading">Loading your orders...</div>
      </div>
    );
  }

  return (
    <div className="my-orders-page fade-in">
      <BackButton onClick={onBackClick} />
      <div className="page-title-header">
        <h1>My Orders</h1>
        <p className="orders-count">
          You have {orders.length} order{orders.length !== 1 ? 's' : ''}
        </p>
      </div>

      {orders.length === 0 ? (
        <div className="empty-orders">
          <div className="empty-icon">📦</div>
          <p>You haven't placed any orders yet.</p>
          <p className="sub-text">Start shopping and place your first order!</p>
        </div>
      ) : (
        <div className="orders-container">
          {orders.map((order) => {
            const status = (order.status || 'placed').toLowerCase();
            const statusIdx = Math.max(0, STATUS_STEPS.indexOf(status));
            const method = order.payment_method || 'cod';
            const payStatus = (order.payment_status || 'unpaid').toLowerCase();
            const payBadgeClass =
              payStatus === 'paid'
                ? 'paid'
                : payStatus === 'pending_verification'
                  ? 'pending'
                  : method === 'online'
                    ? 'online'
                    : 'cod';

            return (
              <div key={order.id} className="order-card">
                <div className="order-header-card">
                  <div className="order-info">
                    <h3>Order #{order.id}</h3>
                    <p className="order-date">{new Date(order.created_at).toLocaleString()}</p>
                  </div>
                  <div className="order-status">
                    <span className={`status-badge ${status}`}>
                      <FontAwesomeIcon icon={statusIcon(status)} />{' '}
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="order-track">
                  {STATUS_STEPS.map((step, i) => (
                    <React.Fragment key={step}>
                      <div className={`track-step ${i <= statusIdx ? 'done' : ''}`}>
                        <span className="track-dot" />
                        <span className="track-label">{step}</span>
                      </div>
                      {i < STATUS_STEPS.length - 1 && (
                        <div className={`track-line ${i < statusIdx ? 'done' : ''}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                <div className="order-meta-row">
                  <span className={`pay-badge ${payBadgeClass}`}>
                    {payLabel(method)} · {paymentStatusLabel(order)}
                  </span>
                </div>

                {order.items && order.items.length > 0 && (
                  <div className="order-items">
                    <div className="items-header">Items</div>
                    {order.items.map((item, index) => (
                      <div key={index} className="order-item">
                        <div className="item-details">
                          <p className="item-name">{item.title || item.name}</p>
                          <p className="item-meta">Quantity: {item.quantity}</p>
                        </div>
                        <p className="item-price">Rs. {(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="order-footer">
                  <div className="order-total">
                    <strong>Total:</strong>
                    <strong>Rs. {parseFloat(order.total).toFixed(2)}</strong>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyOrders;
